import {ChangeDetectionStrategy, Component, OnInit, Input, ViewChild, AfterViewInit} from '@angular/core';
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {MAT_DATE_RANGE_SELECTION_STRATEGY, MatDatepickerModule, MatDateRangePicker} from '@angular/material/datepicker';
import {MatFormFieldModule} from '@angular/material/form-field';
import {
    DATE_RANGE_LIMIT,
    DATE_RANGE_LIMIT_ERROR,
    HOURS_KEY, INVALID_TIME_ERROR,
    MINUTES_KEY
} from '@nagel-consts';
import { TimeRangeValidators } from '@nagel-validators';
import { CustomDateSelectionStrategy } from './custom-day-selection-strategy';
import { DateTimeRangeService } from '@nagel-services';
import { CUSTOM_DATE_FORMATS } from '@nagel-utils';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { CustomDateAdapter } from './custom-date-adapter';
import { TimeRangePickerComponent } from '../time-range-picker/time-range-picker.component';
import { DateTimePickerValueType } from '@nagel-types';

@Component({
    selector: 'single-date-time-picker',
    templateUrl: 'single-date-time-picker.component.html',
    styleUrl: 'single-date-time-picker.component.scss',
    providers: [
        {
            provide: MAT_DATE_RANGE_SELECTION_STRATEGY,
            useClass: CustomDateSelectionStrategy
        },
        {
            provide: DateAdapter,
            useClass: CustomDateAdapter
        },
        {
            provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS
        },
        DateTimeRangeService
    ],
    imports: [
        MatFormFieldModule,
        MatDatepickerModule,
        FormsModule,
        ReactiveFormsModule,
        TimeRangePickerComponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SingleDateTimePickerComponent implements OnInit, AfterViewInit {
    @Input() storageKey: string = '';
    @Input() value?: DateTimePickerValueType | null;
    @Input() hideEmptyValues: boolean = false;
    @Input() isMenuElement: boolean = false;
    @ViewChild('picker') picker!: MatDateRangePicker<Date>;
    @Input() handleOnSubmit?: (formValue: DateTimePickerValueType | null) => void = () => {};

    dateTimeForm = new FormGroup({
        date: new FormGroup({
            startDate: new FormControl<Date>(new Date()),
            endDate: new FormControl<Date>(new Date())
        }),
        time: new FormGroup({
            startTime: new FormGroup({
                hours: new FormControl<string>('00'),
                minutes: new FormControl<string>('00')
            }, {validators: [TimeRangeValidators.isTimeRangeValidValidator(HOURS_KEY, MINUTES_KEY)]}),
            endTime: new FormGroup({
                hours: new FormControl<string>('23'),
                minutes: new FormControl<string>('59')
            }, {validators: [TimeRangeValidators.isTimeRangeValidValidator(HOURS_KEY, MINUTES_KEY)]})
        })
    });

    constructor(
        public dateTimeRangeService: DateTimeRangeService
    ) {}

    ngOnInit(): void {
        this.dateTimeRangeService.setForm(this.dateTimeForm);

        this.dateTimeRangeService.handleOnInit(this.storageKey, this.isMenuElement, this.value);

        if (!(this.isMenuElement && !this.value)) {
            this.handleOnSubmit && this.handleOnSubmit(this.dateTimeForm.value as DateTimePickerValueType);
        }
    }

    ngAfterViewInit() {
        this.picker.closedStream.subscribe(() => {
            if (!this.isMenuElement) {
                this.dateTimeRangeService.patchFormValues(this.dateTimeForm.value as DateTimePickerValueType);
            }
        });
    }

    isDatePickerDisabled() {
        return false;
    }

    shouldDisableCancelButton = () => {
        //Make a check if there are changes in the form
        return false;
    };

    shouldDisableApplyButton = () => {
        return this.dateTimeRangeService.getDateFormGroup().hasError(DATE_RANGE_LIMIT_ERROR)
     || this.dateTimeRangeService.getStartTimeFormGroup().hasError(INVALID_TIME_ERROR)
     || this.dateTimeRangeService.getEndTimeFormGroup().hasError(INVALID_TIME_ERROR);
    };

    getErrorMessage = () => {
        let message = '';

        if (this.dateTimeRangeService.getDateFormGroup().hasError(DATE_RANGE_LIMIT_ERROR)) {
            message = $localize`Date range exceeds the limit of ${DATE_RANGE_LIMIT} days.`;
        }

        if (this.dateTimeRangeService.getStartTimeFormGroup().hasError(INVALID_TIME_ERROR)) {
            message = $localize`Invalid start time.`;
        }

        if (this.dateTimeRangeService.getEndTimeFormGroup().hasError(INVALID_TIME_ERROR)) {
            message = $localize`Invalid end time.`;
        }

        return message;
    };

    onSubmit = (event: Event) => {
        event.preventDefault();
        if (this.dateTimeForm.value) {
            this.dateTimeRangeService.patchFormValues(this.dateTimeForm.value as DateTimePickerValueType);
            this.handleOnSubmit && this.handleOnSubmit(this.dateTimeForm.value as DateTimePickerValueType);
            this.dateTimeRangeService.saveToLocalStorage(this.storageKey);
        }
    };

    onCancel = (event: Event) => {
        event.preventDefault();

        this.dateTimeForm.reset(this.dateTimeRangeService.currentDateTimeSubject.value);
    };
}